import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Tag, Space, Modal, Form, Input, Select, message, Popconfirm, Checkbox } from 'antd';
import { PlusOutlined, PlayCircleOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { campaignsApi, segmentsApi, templatesApi } from '../services/api';

const Campaigns = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [segments, setSegments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, s, t] = await Promise.all([campaignsApi.list({ limit: 50 }), segmentsApi.list({ limit: 100 }), templatesApi.list({ is_active: true })]);
      setCampaigns(c.data); setSegments(s.data); setTemplates(t.data);
    } catch { message.error('데이터를 불러오는데 실패'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (values) => {
    try {
      const channels = values.channels || ['email'];
      const messages = values.template_id ? channels.map(ch => ({ channel: ch, template_id: values.template_id, subject: values.subject })) : [];
      const data = { ...values, messages };
      delete data.channels;
      delete data.template_id; delete data.subject;
      await campaignsApi.create(data);
      message.success('생성됨'); setIsModalOpen(false); form.resetFields(); fetchAll();
    } catch (e) { message.error(e.response?.data?.detail || '실패'); }
  };

  const handleStart = async (id) => { try { await campaignsApi.start(id); message.success('시작됨'); fetchAll(); } catch (e) { message.error(e.response?.data?.detail || '실패'); } };
  const handleDelete = async (id) => { try { await campaignsApi.delete(id); message.success('삭제됨'); fetchAll(); } catch { message.error('실패'); } };
  const handleDuplicate = async (id) => { try { await campaignsApi.duplicate(id); message.success('복제됨'); fetchAll(); } catch { message.error('실패'); } };

  const getStatusTag = (s) => {
    const c = { draft: 'default', scheduled: 'blue', running: 'green', paused: 'orange', completed: 'purple', cancelled: 'red' };
    const l = { draft: '초안', scheduled: '예약', running: '진행', paused: '정지', completed: '완료', cancelled: '취소' };
    return <Tag color={c[s]}>{l[s]}</Tag>;
  };

  const columns = [
    { title: '캠페인명', dataIndex: 'name', render: (t, r) => <a onClick={() => navigate(`/campaigns/${r.id}`)}>{t}</a> },
    { title: '상태', dataIndex: 'status', render: (s) => getStatusTag(s) },
    { title: '대상', dataIndex: 'total_recipients', render: (c) => c?.toLocaleString() || '-' },
    { title: '발송', dataIndex: 'total_sent', render: (c) => c?.toLocaleString() || 0 },
    { title: '', key: 'action', render: (_, r) => (
      <Space size="small">
        {r.status === 'draft' && <Button type="text" icon={<PlayCircleOutlined />} onClick={() => handleStart(r.id)} />}
        <Button type="text" icon={<CopyOutlined />} onClick={() => handleDuplicate(r.id)} />
        {['draft', 'cancelled'].includes(r.status) && <Popconfirm title="삭제?" onConfirm={() => handleDelete(r.id)}><Button type="text" danger icon={<DeleteOutlined />} /></Popconfirm>}
      </Space>
    )},
  ];

  return (
    <div>
      <div className="page-header"><h1>캠페인 관리</h1></div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setIsModalOpen(true); }}>새 캠페인</Button>
      </div>
      <Table columns={columns} dataSource={campaigns} rowKey="id" loading={loading} />
      <Modal title="새 캠페인" open={isModalOpen} onCancel={() => setIsModalOpen(false)} footer={null} width={600}>
        <Form form={form} layout="vertical" onFinish={handleCreate} initialValues={{ type: 'one_time' }}>
          <Form.Item name="name" label="캠페인명" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="type" label="유형"><Select options={[{ value: 'one_time', label: '일회성' }, { value: 'recurring', label: '반복' }]} /></Form.Item>
          <Form.Item name="channels" label="발송 채널" initialValue={['email']}>
            <Checkbox.Group>
              <Checkbox value="email">📧 이메일</Checkbox>
              <Checkbox value="kakao">💬 카카오톡</Checkbox>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item name="segment_id" label="대상 세그먼트"><Select placeholder="선택" allowClear options={segments.map(s => ({ value: s.id, label: `${s.name} (${s.cached_count}명)` }))} /></Form.Item>
          <Form.Item name="template_id" label="템플릿"><Select placeholder="선택" options={templates.map(t => ({ value: t.id, label: t.name }))} /></Form.Item>
          <Form.Item name="subject" label="제목"><Input /></Form.Item>
          <Form.Item><Space><Button type="primary" htmlType="submit">생성</Button><Button onClick={() => setIsModalOpen(false)}>취소</Button></Space></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Campaigns;
